'use client';

import { Button, cn } from '@repo/ui';
import { Home, RefreshCw } from 'lucide-react';
import Link from 'next/link';
import type React from 'react';
import { LazyLottie } from '../../components/lazy-lottie';

const ANIMATION_SRC = '/lottie/crop-failed.lottie.json';

interface IError500TemplateProps extends Omit<React.ComponentProps<'div'>, 'title'> {
  title: string;
  description: string;
  retryLabel: string;
  homeLabel?: string;
  homeHref?: string;
  digest?: string;
  onRetry: () => void;
}

export function Error500Template({
  className,
  title,
  description,
  retryLabel,
  homeLabel,
  homeHref = '/',
  digest,
  onRetry,
  ...props
}: IError500TemplateProps): React.ReactNode {
  return (
    <div
      className={cn(
        'flex min-h-[100svh] flex-col items-center justify-center gap-5 px-6 py-10 select-none',
        className,
      )}
      {...props}
    >
      <LazyLottie
        src={ANIMATION_SRC}
        loop={false}
        className="w-36 sm:w-44 rounded-2xl overflow-hidden"
      />
      <div className="flex max-w-xl flex-col items-center gap-2 text-center">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h1>
        <p className="text-base text-muted-foreground">{description}</p>
        {digest ? (
          <code className="mt-1 rounded bg-muted px-2 py-1 text-xs text-muted-foreground">
            {digest}
          </code>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button onClick={onRetry}>
          <RefreshCw />
          {retryLabel}
        </Button>
        {homeLabel ? (
          <Button asChild variant="outline">
            <Link href={homeHref}>
              <Home />
              {homeLabel}
            </Link>
          </Button>
        ) : null}
      </div>
    </div>
  );
}
